import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, getSecondaryAuth } from "../config/config-firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import API_BASE from "../config/api";
import BottomTabBar from "../components/BottomTabBar";
import { countryCodes } from "../constants/countryCodes";
import { ArrowLeft, Check, X, Phone, Mail, ShieldCheck } from "lucide-react";
import { getStoreId } from "../hooks/useMerchantStores";

const RECAPTCHA_CONTAINER_ID = "recaptcha-container-team-member";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const normalizePhoneDigits = (value) => String(value || "").replace(/\D/g, "");

export default function AddTeamMember() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    teamMembers = [],
    stores = [],
    numericUserId = null,
    defaultStoreId = null,
  } = location.state || {};

  const [addForm, setAddForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "+1",
    mobile: "",
    username: "",
    password: "",
    storeId: defaultStoreId ? String(defaultStoreId) : "",
  });

  const [addFieldErrors, setAddFieldErrors] = useState({});
  const [actionError, setActionError] = useState("");
  const [saving, setSaving] = useState(false);

  // OTP State
  const [otpStatus, setOtpStatus] = useState("idle"); // "idle" | "sent" | "verified"
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [teamMemberIdToken, setTeamMemberIdToken] = useState(null);

  const recaptchaVerifierRef = useRef(null);
  const confirmationResultRef = useRef(null);

  const teardownRecaptcha = () => {
    try {
      recaptchaVerifierRef.current?.clear?.();
    } catch (err) {
      console.error("Error clearing recaptcha:", err);
    }
    recaptchaVerifierRef.current = null;
    const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
    if (container) container.innerHTML = "";
  };

  const resetOtpState = () => {
    setOtpStatus("idle");
    setOtpCode("");
    setOtpError("");
    setTeamMemberIdToken(null);
    confirmationResultRef.current = null;
    teardownRecaptcha();
  };

  useEffect(() => {
    return () => teardownRecaptcha();
  }, []);

  const updateAddFormMobileField = (field, value) => {
    setAddForm((p) => ({ ...p, [field]: value }));
    if (otpStatus !== "idle") {
      resetOtpState();
    }
  };

  const handleSendOtp = async () => {
    const mobileDigits = addForm.mobile.trim();
    if (!mobileDigits) {
      setOtpError("Please enter a mobile number.");
      return;
    }

    const fullMobileDigits = normalizePhoneDigits(`${addForm.countryCode}${mobileDigits}`);
    const mobileAlreadyUsed = teamMembers.some(
      (m) => normalizePhoneDigits(m.phoneNumber || m.mobile) === fullMobileDigits
    );
    if (mobileAlreadyUsed) {
      setOtpError("This phone number is already registered.");
      return;
    }

    setSendingOtp(true);
    setOtpError("");
    try {
      const secondaryAuth = getSecondaryAuth();

      if (!recaptchaVerifierRef.current) {
        const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
        if (!container) throw new Error("Recaptcha container not found.");
        recaptchaVerifierRef.current = new RecaptchaVerifier(secondaryAuth, container, {
          size: "invisible",
          callback: () => {},
        });
      }

      const fullMobile = `${addForm.countryCode}${mobileDigits}`;
      const result = await signInWithPhoneNumber(
        secondaryAuth,
        fullMobile,
        recaptchaVerifierRef.current,
      );
      confirmationResultRef.current = result;
      setOtpStatus("sent");
    } catch (err) {
      console.error("Error sending OTP:", err);
      setOtpError(err.message || "Failed to send OTP.");
      teardownRecaptcha();
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    const code = otpCode.trim();
    if (!code || !confirmationResultRef.current) return;

    setVerifyingOtp(true);
    setOtpError("");
    try {
      const result = await confirmationResultRef.current.confirm(code);
      const idToken = await result.user.getIdToken();
      setTeamMemberIdToken(idToken);
      setOtpStatus("verified");
      await getSecondaryAuth().signOut();
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setOtpError(err.message || "Invalid code.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleAddMember = async () => {
    const firstName = addForm.firstName.trim();
    const lastName = addForm.lastName.trim();
    const username = addForm.username.trim();
    const password = addForm.password;
    const mobileDigits = addForm.mobile.trim();
    const email = addForm.email.trim();

    if (!firstName || !lastName || !username || !password || !mobileDigits || !email || !addForm.storeId) return;
    if (!EMAIL_PATTERN.test(email)) {
      setActionError("Please enter a valid email address.");
      return;
    }

    const emailAlreadyUsed = teamMembers.some(
      (m) => (m.email || "").trim().toLowerCase() === email.toLowerCase()
    );
    if (emailAlreadyUsed) {
      setAddFieldErrors({ email: "This email is already registered." });
      return;
    }

    const fullMobileDigits = normalizePhoneDigits(`${addForm.countryCode}${mobileDigits}`);
    const mobileAlreadyUsed = teamMembers.some(
      (m) => normalizePhoneDigits(m.phoneNumber || m.mobile) === fullMobileDigits
    );
    if (mobileAlreadyUsed) {
      setAddFieldErrors({ mobile: "This phone number is already registered." });
      return;
    }

    if (!numericUserId || stores.length === 0) {
      setActionError("Loading stores... please try again.");
      return;
    }

    if (!addForm.storeId) {
      setActionError("Select a store for this team member.");
      return;
    }

    if (otpStatus !== "verified" || !teamMemberIdToken) {
      setActionError("Please verify the mobile number first.");
      return;
    }

    setSaving(true);
    setActionError("");
    setAddFieldErrors({});
    try {
      const mobile = `${addForm.countryCode}${mobileDigits}`;

      const res = await fetch(`${API_BASE}/saveTeamMember`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          storeId: Number(addForm.storeId),
          token: teamMemberIdToken,
          firstName,
          lastName,
          email,
          phoneNumber: mobile,
          username,
          password,
          userId: Number(numericUserId),
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        if (Array.isArray(json.errors) && json.errors.length > 0) {
          const fieldErrors = {};
          json.errors.forEach((e) => {
            if (e?.field && e?.message) fieldErrors[e.field] = e.message;
          });
          setAddFieldErrors(fieldErrors);
          return;
        }

        const lowerMessage = `${json.message || ""} ${json.error || ""}`.toLowerCase();
        const fallbackErrors = {};

        if (lowerMessage.includes("email")) {
          fallbackErrors.email = "This email is already registered.";
        }

        if (
          lowerMessage.includes("phone") ||
          lowerMessage.includes("mobile") ||
          lowerMessage.includes("firebase") ||
          lowerMessage.includes("token") ||
          lowerMessage.includes("already-exists") ||
          (lowerMessage.includes("already exists") && !lowerMessage.includes("email"))
        ) {
          fallbackErrors.mobile = "This phone number is already registered.";
        }

        if (Object.keys(fallbackErrors).length > 0) {
          setAddFieldErrors(fallbackErrors);
          return;
        }

        throw new Error(json.message || "Failed to add team member.");
      }

      navigate("/team-members", { replace: true });
    } catch (err) {
      console.error("Error adding team member:", err);
      setActionError(err.message || "Failed to add team member.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans pb-24">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 sm:px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 rounded-full hover:bg-gray-50 text-gray-700"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-lg font-bold text-gray-900">Add Team Member</h1>
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-md mx-auto w-full p-4 sm:p-5">
        {actionError && (
          <div className="mb-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl px-4 py-3">
            {actionError}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-4">
          <div className="flex gap-3">
            <div className="flex-1 min-w-0">
              <label className="text-xs text-gray-600 font-medium mb-1 block">First name</label>
              <input
                type="text"
                placeholder="First name"
                value={addForm.firstName}
                onChange={(e) => setAddForm((p) => ({ ...p, firstName: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1 min-w-0">
              <label className="text-xs text-gray-600 font-medium mb-1 block">Last name</label>
              <input
                type="text"
                placeholder="Last name"
                value={addForm.lastName}
                onChange={(e) => setAddForm((p) => ({ ...p, lastName: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium mb-1 block">Username</label>
            <input
              type="text"
              placeholder="Enter username"
              value={addForm.username}
              onChange={(e) => setAddForm((p) => ({ ...p, username: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium mb-1 block">Password</label>
            <input
              type="password"
              placeholder="Set a password"
              value={addForm.password}
              onChange={(e) => setAddForm((p) => ({ ...p, password: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium mb-1 block">Store</label>
            <select
              value={addForm.storeId}
              onChange={(e) => setAddForm((p) => ({ ...p, storeId: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
            >
              <option value="" disabled>Select a store</option>
              {stores.map((store) => {
                const id = getStoreId(store);
                if (!id) return null;
                return (
                  <option key={id} value={id}>
                    {store.full_name || store.name || `Store #${id}`}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium mb-1 block">Mobile number</label>
            <div className="flex gap-2">
              <select
                value={addForm.countryCode}
                onChange={(e) => updateAddFormMobileField("countryCode", e.target.value)}
                disabled={otpStatus !== "idle"}
                className="border border-gray-200 rounded-xl px-2 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
              >
                {countryCodes.map((c) => (
                  <option key={c.code} value={c.dial_code}>
                    {c.dial_code}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="Enter mobile number"
                value={addForm.mobile}
                onChange={(e) => updateAddFormMobileField("mobile", e.target.value)}
                disabled={otpStatus !== "idle"}
                className="flex-1 min-w-0 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>
            {addFieldErrors.mobile && (
              <p className="text-[11px] text-red-500 mt-1.5">{addFieldErrors.mobile}</p>
            )}

            <div className="mt-3">
              {otpStatus === "idle" && (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={!addForm.mobile.trim() || sendingOtp}
                  className="w-full border border-blue-200 text-blue-600 text-xs font-semibold py-2.5 rounded-xl disabled:opacity-50"
                >
                  {sendingOtp ? "Sending OTP…" : "Send OTP to verify number"}
                </button>
              )}

              {otpStatus === "sent" && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      inputMode="numeric"
                      placeholder="Enter OTP"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                      className="flex-1 min-w-0 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={!otpCode.trim() || verifyingOtp}
                      className="bg-blue-600 text-white text-xs font-bold px-4 rounded-xl disabled:opacity-50"
                    >
                      {verifyingOtp ? "Verifying…" : "Verify"}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={sendingOtp}
                    className="text-[11px] text-blue-600 font-medium"
                  >
                    {sendingOtp ? "Resending…" : "Resend OTP"}
                  </button>
                </div>
              )}

              {otpStatus === "verified" && (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-green-600 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
                  <ShieldCheck size={14} />
                  Mobile number verified
                </div>
              )}

              {otpError && (
                <p className="text-[11px] text-red-500 mt-1.5">{otpError}</p>
              )}
            </div>

            <div id={RECAPTCHA_CONTAINER_ID} />
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium mb-1 block">Email</label>
            <input
              type="email"
              placeholder="Enter email address"
              value={addForm.email}
              onChange={(e) => setAddForm((p) => ({ ...p, email: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            {addFieldErrors.email && (
              <p className="text-[11px] text-red-500 mt-1.5">{addFieldErrors.email}</p>
            )}
          </div>

          <button
            type="button"
            onClick={handleAddMember}
            disabled={
              !addForm.firstName.trim() ||
              !addForm.lastName.trim() ||
              !addForm.username.trim() ||
              !addForm.password ||
              !addForm.mobile.trim() ||
              !addForm.email.trim() ||
              !addForm.storeId ||
              otpStatus !== "verified" ||
              saving
            }
            className="w-full bg-blue-600 text-white font-bold text-sm py-4 rounded-full mt-6 disabled:opacity-40 flex items-center justify-center gap-2"
          >
            <Check size={15} />
            {saving ? "Adding…" : "Add member"}
          </button>
        </div>
      </div>
      <BottomTabBar />
    </div>
  );
}
